import React, { useState, useEffect, useRef } from 'react';
import MainLayout from '../../components/Layout/MainLayout.tsx';
import CollectionFilters from '../../components/Collections/CollectionFilters.tsx';
import CollectionTable from '../../components/Collections/CollectionTable.tsx';
import NewCollectionModal from '../../components/modals/NewCollectionModal.tsx';
import CollectionNotification from '../../components/Collections/CollectionNotification.tsx';
import { Collection, CollectionFilterOptions, CollectionPaginationOptions, CollectionStatus } from '../../types/collections';
import { partnersData } from '../../data/partnersData.ts';
import { Send, Clock, User, Calendar, Hash, X, DollarSign } from 'react-feather';

// Simplified currency map (can be moved to shared utils/data later)
const currencyMap: { [key: string]: string } = {
    'UAE': 'AED',
    'KSA': 'SAR',
    'Qatar': 'QAR',
    'Kuwait': 'KWD',
    'Bahrain': 'BHD',
    'Egypt': 'EGP',
    'Oman': 'OMR',
};

// *** NEW: Partner Location Mapping (similar to TransactionsPage) ***
const partnerLocations: { [key: string]: string } = {
  'Amazon': 'UAE',
  'Shopify': 'KSA',
  'Noon': 'UAE',
  'Namshi': 'KSA',
  'Carrefour': 'UAE',
  'IKEA': 'KSA',
  'Adidas': 'UAE',
  'H&M': 'Qatar',
  'Xcite': 'Kuwait',
  'Jarir Bookstore': 'Kuwait',
  'Sharaf DG': 'Bahrain',
  'LuLu Hypermarket': 'Bahrain',
  'Talabat': 'Qatar',
  // Add missing partners from partnersData.ts
  'Centrepoint': 'UAE',
  'Pottery Barn': 'KSA',
  'West Elm': 'Kuwait',
  'Sephora': 'UAE',
  'Souq (Amazon EG)': 'Egypt',
  'B.Tech': 'Egypt',
  'Raya Store': 'Egypt',
  'Extra Stores': 'Oman',
  // Remove unused default
  // 'Default Partner': 'UAE', 
};

// Collection Details Panel Component
interface CollectionDetailsPanelProps {
  collection: Collection | null;
  onClose: () => void;
  isVisible: boolean;
  contentFadeKey: string;
}

const CollectionDetailsPanel: React.FC<CollectionDetailsPanelProps> = ({ collection, onClose, isVisible, contentFadeKey }) => {
  // Create ref for the panel element
  const panelRef = useRef<HTMLDivElement>(null);
  
  // Add state for content fade animation
  const [contentVisible, setContentVisible] = useState(true);
  
  // Handle collection changes with animation
  useEffect(() => {
    setContentVisible(true);
  }, [contentFadeKey]);
  
  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Skip outside click handling if we clicked on a table row
      if ((event.target as Element).closest('tr')) {
        return;
      }
      
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    
    // Only add the event listener if the panel is visible
    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible, onClose]);

  // Early return after hooks are called
  if (!collection) return null;

  // Format dates for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric'
    });
  };

  // Calculate days until due
  const calculateDaysUntilDue = () => {
    const dueDate = new Date(collection.dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffTime = dueDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysUntilDue = calculateDaysUntilDue();

  // Format currency
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <div 
      ref={panelRef}
      style={{
        position: 'fixed',
        top: '64px', // Adjusted to leave space for header
        right: 0,
        width: '380px',
        height: 'calc(100vh - 64px)', // Adjusted height to account for header
        backgroundColor: 'white',
        boxShadow: '-4px 0 10px rgba(0, 0, 0, 0.1)',
        zIndex: 1000,
        overflowY: 'auto',
        transform: isVisible ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.2s ease-out',
        padding: '24px',
      }}
    >
      {/* Header with close button */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <h2 style={{ 
          margin: 0, 
          fontSize: '24px', 
          fontWeight: 600, 
          color: '#111827',
          fontFamily: 'inherit'
        }}>
          Collection Details
        </h2>
        <button 
          onClick={onClose} 
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <X size={20} />
        </button>
      </div>

      {/* Panel Content with Fade Animation */}
      <div style={{
        opacity: contentVisible ? 1 : 0,
        transition: 'opacity 0.15s ease-in-out',
      }}>
        {/* Collection Status Badge */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
          <span style={{
            backgroundColor: collection.status === 'Pending' ? '#fef3c7' :
                            collection.status === 'Sent' ? '#e0f2fe' :
                            collection.status === 'Paid' ? '#d1fae5' : 
                            '#fee2e2', // Overdue
            color: collection.status === 'Pending' ? '#92400e' :
                  collection.status === 'Sent' ? '#0369a1' :
                  collection.status === 'Paid' ? '#047857' : 
                  '#b91c1c', // Overdue
            padding: '6px 12px',
            borderRadius: '16px',
            fontSize: '14px',
            fontWeight: 500,
            fontFamily: 'inherit',
            textTransform: 'capitalize',
          }}>
            {collection.status}
          </span>
        </div>

        {/* Amount Section */}
        <div style={{ marginBottom: '32px' }}>
          <p style={{ 
            color: '#6b7280', 
            fontSize: '16px', 
            margin: '0 0 8px 0',
            fontWeight: 500,
            fontFamily: 'inherit'
          }}>
            Amount
          </p>
          <h3 style={{ 
            fontSize: '32px', 
            fontWeight: 600, 
            margin: 0,
            color: '#111827',
            fontFamily: 'inherit'
          }}>
            {formatCurrency(collection.amount, collection.currency)}
          </h3>
        </div>

        {/* Due Date & Status Indicator */}
        <div style={{ 
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <Calendar size={20} color="#6b7280" />
          <div>
            <p style={{ 
              fontSize: '14px', 
              margin: '0 0 4px 0',
              fontFamily: 'inherit',
              color: '#6b7280',
            }}>
              Due Date
            </p>
            <div style={{ 
              fontSize: '15px', 
              fontWeight: 500,
              fontFamily: 'inherit',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}>
              {formatDate(collection.dueDate)}
              <span style={{
                fontSize: '13px',
                padding: '2px 6px',
                borderRadius: '12px',
                backgroundColor: daysUntilDue < 0 ? '#fee2e2' : 
                                daysUntilDue <= 7 ? '#fef3c7' : '#f3f4f6',
                color: daysUntilDue < 0 ? '#dc2626' : 
                      daysUntilDue <= 7 ? '#92400e' : '#6b7280',
                fontWeight: 500,
              }}>
                {daysUntilDue < 0 ? `${Math.abs(daysUntilDue)} days overdue` : 
                daysUntilDue === 0 ? 'Due today' : 
                `${daysUntilDue} days left`}
              </span>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div style={{ height: '1px', backgroundColor: '#e5e7eb', margin: '24px 0' }}></div>

        {/* Collection Details */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div style={{ width: '48%' }}>
              <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0', fontFamily: 'inherit' }}>Partner</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ 
                  color: '#111827', 
                  fontSize: '15px', 
                  fontWeight: 500,
                  fontFamily: 'inherit'
                }}>
                  {collection.partnerName}
                </div>
              </div>
            </div>
            <div style={{ width: '48%' }}>
              <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0', fontFamily: 'inherit' }}>Country</p>
              <div style={{ 
                color: '#111827', 
                fontSize: '15px', 
                fontWeight: 500,
                fontFamily: 'inherit',
              }}>
                {collection.country}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div style={{ width: '48%' }}>
              <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0', fontFamily: 'inherit' }}>Created at</p>
              <div style={{ 
                color: '#111827', 
                fontSize: '15px', 
                fontWeight: 500,
                fontFamily: 'inherit'
              }}>
                {formatDate(collection.createdAt)}
              </div>
            </div>
            <div style={{ width: '48%' }}>
              <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0', fontFamily: 'inherit' }}>Reference</p>
              <div style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <Hash size={16} color="#9ca3af" />
                <span style={{ 
                  color: '#111827', 
                  fontSize: '15px', 
                  fontWeight: 500,
                  fontFamily: 'inherit'
                }}>
                  {collection.reference}
                </span>
              </div>
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0', fontFamily: 'inherit' }}>Product</p>
            <div style={{ 
              color: '#111827', 
              fontSize: '15px', 
              fontWeight: 500,
              fontFamily: 'inherit'
            }}>
              {collection.product}
            </div>
          </div>
        </div>

        {/* Additional Information Section */}
        <div>
          <h4 style={{ 
            fontSize: '16px', 
            color: '#6b7280', 
            fontWeight: 500, 
            margin: '0 0 16px 0',
            fontFamily: 'inherit'
          }}>
            Collection Status Timeline
          </h4>

          <div style={{ 
            backgroundColor: '#f9fafb', 
            borderRadius: '8px', 
            padding: '16px' 
          }}>
            {/* Created timestamp */}
            <div style={{ 
              display: 'flex', 
              gap: '12px',
              marginBottom: '12px'
            }}>
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center' 
              }}>
                <div style={{ 
                  width: '10px', 
                  height: '10px', 
                  borderRadius: '50%', 
                  backgroundColor: '#10b981',
                  marginBottom: '4px'
                }}></div>
                <div style={{ 
                  width: '2px', 
                  height: '100%', 
                  backgroundColor: '#e5e7eb',
                  flex: 1
                }}></div>
              </div>
              <div>
                <p style={{ 
                  fontSize: '14px', 
                  fontWeight: 500, 
                  color: '#111827', 
                  margin: '0 0 2px 0',
                  fontFamily: 'inherit'
                }}>
                  Collection Created
                </p>
                <p style={{ 
                  fontSize: '13px', 
                  color: '#6b7280', 
                  margin: 0,
                  fontFamily: 'inherit'
                }}>
                  {new Date(collection.createdAt).toLocaleString()}
                </p>
              </div>
            </div>

            {/* Status update timestamp (if not pending) */}
            {collection.status !== 'Pending' && (
              <div style={{ 
                display: 'flex', 
                gap: '12px'
              }}>
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center' 
                }}>
                  <div style={{ 
                    width: '10px', 
                    height: '10px', 
                    borderRadius: '50%', 
                    backgroundColor: collection.status === 'Paid' ? '#10b981' : 
                                    collection.status === 'Sent' ? '#3b82f6' : '#ef4444'
                  }}></div>
                </div>
                <div>
                  <p style={{ 
                    fontSize: '14px', 
                    fontWeight: 500, 
                    color: '#111827', 
                    margin: '0 0 2px 0',
                    fontFamily: 'inherit'
                  }}>
                    Status Changed to {collection.status}
                  </p>
                  <p style={{ 
                    fontSize: '13px', 
                    color: '#6b7280', 
                    margin: 0,
                    fontFamily: 'inherit'
                  }}>
                    {new Date(collection.updatedAt).toLocaleString()}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Collection ID Footer */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          marginTop: '40px',
          color: '#9ca3af',
          fontSize: '14px',
          fontFamily: 'inherit'
        }}>
          <Hash size={16} style={{ marginRight: '6px' }} />
          Collection ID: {collection.id}
        </div>
      </div>
    </div>
  );
};

const CollectionsPage: React.FC = () => {
  // State for collections
  const [collections, setCollections] = useState<Collection[]>([]);
  const [filteredCollections, setFilteredCollections] = useState<Collection[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [notification, setNotification] = useState<Collection | null>(null);

  // --- NEW: State for ID copy hover and feedback ---
  const [hoveredIdIcon, setHoveredIdIcon] = useState<string | null>(null);
  const [copiedId, setCopiedId] = React.useState<string | null>(null);

  // Filters state - Set default status to 'All'
  const [filters, setFilters] = useState<CollectionFilterOptions>({
    status: 'All', // Changed default from 'Pending' to 'All'
    search: '',
  });

  // Pagination state - Keep for filtering logic, but component removed
  const [pagination, setPagination] = useState<CollectionPaginationOptions>({
    page: 1,
    limit: 10,
    total: 0,
  });

  // --- NEW: Selected collection state ---
  const [selectedCollection, setSelectedCollection] = useState<Collection | null>(null);
  // --- NEW: Panel visibility state for animation ---
  const [isPanelVisible, setIsPanelVisible] = useState<boolean>(false);
  // --- NEW: Content fade key for collection changes ---
  const [contentFadeKey, setContentFadeKey] = useState<string>('');

  // Load and transform partner data into collections
  useEffect(() => {
    // Define product options
    const productOptions = ['BNPL', 'Tabby Card', 'Ads Postpaid'];

    const generatedCollections = partnersData.map((partner, index) => {
      const id = (index + 1).toString();
      const statusOptions: CollectionStatus[] = ['Pending', 'Sent', 'Paid', 'Overdue'];
      const status = statusOptions[Math.floor(Math.random() * statusOptions.length)];
      const createdAt = new Date();
      createdAt.setDate(createdAt.getDate() - Math.floor(Math.random() * 30)); // Random date in last 30 days
      const dueDate = new Date(createdAt);
      dueDate.setDate(dueDate.getDate() + 15 + Math.floor(Math.random() * 30)); // Due 15-45 days after creation
      const updatedAt = status === 'Pending' ? createdAt : new Date(createdAt);
      if (status !== 'Pending') {
          updatedAt.setDate(updatedAt.getDate() + Math.floor(Math.random() * 10)); // Updated within 10 days if not pending
      }

      // Basic amount generation - refine as needed
      let amount = partner.baseDebit ? partner.baseDebit * 0.01 : 5000; // Use 1% of baseDebit or 5000
      amount = Math.floor(amount * (Math.random() * 0.5 + 0.75)); // Randomize amount slightly
      
      const partnerName = partner.name; // Use name from partnerData
      const country = partnerLocations[partnerName] || 'N/A'; // Get country from map or fallback
      // Assign a random product from the options
      const product = productOptions[Math.floor(Math.random() * productOptions.length)];

      return {
        id,
        partnerId: partnerName, // Using name as ID like in the modal
        partnerName,
        amount: Math.max(500, Math.round(amount)), // Ensure minimum amount
        currency: currencyMap[partner.location] || 'AED', // Use mapped currency or default
        status,
        dueDate: dueDate.toISOString().split('T')[0], // Format as YYYY-MM-DD
        reference: `COLL-${createdAt.getFullYear()}-${String(createdAt.getMonth() + 1).padStart(2, '0')}-${String(id).padStart(3, '0')}`,
        createdAt: createdAt.toISOString(),
        updatedAt: updatedAt.toISOString(),
        country, // Add country
        product, // Add product
      };
    });
    setCollections(generatedCollections);
  }, []);

  // Apply filters
  useEffect(() => {
    let result = [...collections];

    // Filter by status
    if (filters.status !== 'All') {
      result = result.filter((collection) => collection.status === filters.status);
    }

    // Filter by search term (Example: search by partner name, reference, ID)
    if (filters.search) {
        const searchTermLower = filters.search.toLowerCase();
        result = result.filter(collection => 
            collection.partnerName.toLowerCase().includes(searchTermLower) ||
            collection.reference.toLowerCase().includes(searchTermLower) ||
            collection.id.toLowerCase().includes(searchTermLower)
            // Add more fields to search if needed (e.g., amount, country, product)
        );
    }

    // Update total count *before* pagination (for potential future pagination re-add)
    setPagination((prev) => ({ ...prev, total: result.length }));

    // Set all filtered results directly to the table
    setFilteredCollections(result);

  }, [collections, filters, pagination.page, pagination.limit]); // Keep page/limit dependencies for now

  // Handle filter changes
  const handleFilterChange = (newFilters: Partial<CollectionFilterOptions>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPagination((prev) => ({ ...prev, page: 1 })); // Reset to first page on filter change
  };


  // --- NEW: Handler for copying ID ---
  const handleCopyId = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500); // Reset icon after 1.5 seconds
    } catch (err) {
      console.error('Failed to copy ID: ', err);
      // Optionally: show an error message to the user
    }
  };

  // Handle sending a collection (keep existing logic)
  const handleSendCollection = (collection: Collection) => {
    // In a real app, this would send an API request to update the collection status
    const updatedCollection = {
      ...collection,
      status: 'Sent' as CollectionStatus,
      updatedAt: new Date().toISOString()
    };

    const updatedCollections = collections.map((c) => {
      if (c.id === collection.id) {
        return updatedCollection;
      }
      return c;
    });

    setCollections(updatedCollections);

    // Show notification
    setNotification(updatedCollection);
  };

  // Handle creating a new collection
  const handleCreateCollection = (collectionData: Omit<Collection, 'id' | 'status' | 'createdAt' | 'updatedAt' | 'country' | 'product' >) => {
    // Define product options (can be defined outside if preferred)
    const productOptions = ['BNPL', 'Tabby Card', 'Ads Postpaid'];
    const newCollection: Collection = {
      id: (collections.length + 1).toString(),
      ...collectionData,
      status: 'Pending' as CollectionStatus,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      // Derive country and assign random product for new collection
      country: partnerLocations[collectionData.partnerName] || 'N/A', 
      product: productOptions[Math.floor(Math.random() * productOptions.length)], // Assign random product
    };

    setCollections([newCollection, ...collections]);

    // Show notification for new collection
    setNotification(newCollection);
  };

  // Handle notification close
  const handleNotificationClose = () => {
    setNotification(null);
  };

  // --- NEW: Handler for row click ---
  const handleRowClick = (collection: Collection) => {
    if (selectedCollection) {
      // If a collection is already selected (panel is open), just update the collection
      // and trigger content fade animation with new key
      setContentFadeKey(collection.id);
      setSelectedCollection(collection);
    } else {
      // First time opening the panel
      setSelectedCollection(collection);
      setContentFadeKey(collection.id);
      // Set panel to visible with slight delay for proper animation when opening
      setTimeout(() => setIsPanelVisible(true), 10);
    }
  };

  // --- NEW: Handler to close the details panel ---
  const handleCloseDetailsPanel = () => {
    // First hide panel with animation
    setIsPanelVisible(false);
    // Then remove collection data after animation completes
    setTimeout(() => setSelectedCollection(null), 200);
  };

  return (
    <MainLayout>
      <div style={{ padding: '24px', backgroundColor: '#ffffff', minHeight: '100vh' }}>
        <div style={{ marginBottom: '24px' }}>
          <div style={{ marginBottom: '20px' }}>
            <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 600, color: '#111827', lineHeight: '1.2' }}>Collections</h1>
            <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '14px' }}>Manage collection requests from partners</p>
          </div>
        </div>

        {/* NEW: Flex container for filters and button */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <CollectionFilters filters={filters} onFilterChange={handleFilterChange} />
          <button
            onClick={() => setIsModalOpen(true)}
            style={{
              backgroundColor: '#fff',
              color: '#1f2937',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '10px 15px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
              transition: 'background-color 0.2s ease, border-color 0.2s ease',
              marginLeft: '16px', // Add some margin for spacing
            }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#f9fafb')}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#fff')}
          >
            <Send size={16} />
            New Collection
          </button>
        </div>

        <CollectionTable 
          collections={filteredCollections} 
          copiedId={copiedId}
          hoveredIdIcon={hoveredIdIcon}
          setHoveredIdIcon={setHoveredIdIcon}
          onCopyId={handleCopyId}
          onRowClick={handleRowClick}
        />

      </div>

      {selectedCollection && (
        <CollectionDetailsPanel 
          collection={selectedCollection} 
          onClose={handleCloseDetailsPanel}
          isVisible={isPanelVisible}
          contentFadeKey={contentFadeKey}
        />
      )}

      <NewCollectionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleCreateCollection}
      />

      {notification && (
        <CollectionNotification
          collection={notification}
          onClose={handleNotificationClose}
        />
      )}
    </MainLayout>
  );
};

export default CollectionsPage;
